import { $, qs, qsa } from '../ui/dom';
import { showToast } from '../ui/toast';
import { segAvatarsHtml, deviceDisplayName } from '../ui/animals';
import { team } from '../state/team';
import { colorForToken } from '../state/cohort-model';
import { getAuthUserId } from '../state/auth-state';
import { state, getLeafletMap } from '../state/app-state';
import { MOCK } from '../state/device';
import {
  clearStoredToken,
  forgetFromHistory, clearKeypair, clearActiveSession,
} from '../storage/persistence';
import {
  ensureMap, drawRoutes,
  getPendingMapFit, setPendingMapFit,
} from '../map/tracker';
import { ensureWorldMap, startWorldLive, stopWorldLive, getWorldMap } from '../map/world';
import {
  startConsolePoll, stopConsolePoll,
  renderTeamLayer,
} from '../map/team-layer';
import { stopCommandConsole } from '../ui/command-console';
import { renderAccountPanel, refreshMyClaims } from '../ui/account';
import { renderDevicePanel } from '../ui/device-panel';
import { renderRecentDevices, renderSlots, refreshRecentDevicesButton } from '../ui/picker';
import { updateRecordUi, stopRecording } from '../ui/recording';
import { renderDeviceHeader, renderSessions } from '../ui/sidebar';
import { enterAdmin } from '../ui/admin';
import { startCohortLive, stopCohortLive } from '../routing/polling';

/* ─────────────────────────────────────────────
   NAVIGATION DRAWER (right-anchored; replaces the ⋮ popover)
   One |||-triggered drawer shared by both screens. `nav` lists destinations;
   Map / New device leave the drawer via show(); the lighter surfaces
   (Devices, Account, Device, Settings, a shared-link entry) swap in as
   sub-views behind a Back affordance, keeping the map visible behind the scrim.
   ───────────────────────────────────────────── */
export type DrawerView = 'nav' | 'devices' | 'account' | 'device' | 'settings' | 'shared';
export let drawerView: DrawerView = 'nav';
export const drawerEl = $('drawer');
export const drawerScrim = $('drawer-scrim');

let _renderSettingsPanelCb: (() => void) | null = null;
export function setRenderSettingsPanelCb(fn: () => void){ _renderSettingsPanelCb = fn; }

export function show(which: string){
  qsa('.screen').forEach(s => s.classList.remove('is-active'));
  $('screen-'+which).classList.add('is-active');
  if(which === 'map'){
    renderDeviceHeader(); renderSessions();
    setTimeout(()=>{
      ensureMap();
      if(getLeafletMap()) getLeafletMap().invalidateSize();
      // A device hydrated behind a #/world deep-link draws its trail here, the
      // first time the user actually opens the (now-sized) tracker map (H1).
      if(getPendingMapFit()){ setPendingMapFit(false); drawRoutes({ fit: true }); }
      // If a console poll resolved before this deferred ensureMap(), paint the
      // already-fetched team overlays now (otherwise the next 30 s poll does it).
      if(team.seeded) renderTeamLayer();
    }, 60);
    updateRecordUi();
    startCohortLive();
    // Additive team-overlay poll (markers/geofences/breaches). Gates internally
    // on signed-in + claimed-participant; a no-op otherwise.
    startConsolePoll();
    // leaving the world map (e.g. via the back affordance) stops its poll.
    stopWorldLive();
    location.hash = '#/map';
  } else if(which === 'world'){
    location.hash = '#/world';
    // The world map is its own instance; create it lazily and size it after the
    // screen is visible (a hidden div has zero size, so invalidateSize() must
    // run post-show, like the tracker map). startWorldLive() handles the
    // auth gate + first fetch + 30 s poll.
    setTimeout(()=>{ ensureWorldMap(); if(getWorldMap()) getWorldMap().invalidateSize(); startWorldLive(); }, 60);
    // entering world: end any active recording / tracker feed, like the picker.
    stopRecording();
    stopCohortLive();
    stopConsolePoll();
  } else if(which === 'admin'){
    location.hash = '#/admin';
    // Operator-only console; behaves like the world map re: live feeds — end
    // recording + polls. enterAdmin() does the auth-gated stats fetch and
    // renders (or shows "not authorized" when the API 404s a non-admin).
    stopRecording();
    stopCohortLive();
    stopConsolePoll();
    stopWorldLive();
    enterAdmin();
  } else {
    // entering the picker: a create/forget elsewhere may have changed whether
    // there are devices to offer, so re-evaluate the "recent devices" button
    refreshRecentDevicesButton();
    // leaving map: stop any active recording loop and cohort live feed.
    // Web has no background recording, so navigating away ends the session
    // cleanly (DECIDE-5) — surface a toast rather than blocking the nav.
    const wasRecording = state.recording || state.geoRequesting;
    stopRecording();
    stopCohortLive();
    stopConsolePoll();
    stopWorldLive();
    if(wasRecording) showToast('recording stopped');
    // Defensive: cancel any lingering Web Animations on the picker
    // (e.g. the "Open device" reveal animation with fill:'forwards')
    // so re-entering #/create always shows it at full opacity.
    const inner = qs('#screen-create .create-inner');
    if(inner) inner.getAnimations().forEach((a: any) => a.cancel());
  }
  window.scrollTo(0,0);
}

export function openDrawer(view: DrawerView = 'nav'){
  setDrawerView(view);
  drawerEl.classList.add('is-open');
  drawerScrim.classList.add('is-open');
  drawerEl.setAttribute('aria-hidden', 'false');
}
export function closeDrawer(){
  drawerEl.classList.remove('is-open');
  drawerScrim.classList.remove('is-open');
  drawerEl.setAttribute('aria-hidden', 'true');
  stopCommandConsole();   // tear down the device console poll on close
}
export function setDrawerView(view: DrawerView){
  drawerView = view;
  // The command console only lives in the Device sub-view; tearing it down on
  // any other view stops its poll (it restarts when renderDevicePanel runs).
  if(view !== 'device') stopCommandConsole();
  qsa('.drawer-view', drawerEl).forEach((v: any) => { v.hidden = v.id !== 'drawer-' + view; });
  if(view === 'nav') renderDrawerActive();
  else if(view === 'devices') renderRecentDevices();
  else if(view === 'account'){
    renderAccountPanel();
    // Re-pull the claim list so "My devices" reflects the server, then repaint.
    if(getAuthUserId() != null) refreshMyClaims().then(renderAccountPanel);
  }
  else if(view === 'device') renderDevicePanel();
  else if(view === 'settings') _renderSettingsPanelCb?.();
}

// The active-device card at the top of the nav view → opens the Device sub-view.
export function renderDrawerActive(){
  const el = $('drawer-active');
  if(!el) return;
  if(!MOCK.device){ el.style.display = 'none'; return; }
  const d = MOCK.device;
  const color = colorForToken(d.token);
  const names = deviceDisplayName(d.token, d.animals, d.group, undefined, d.tag);
  const tail = d.token.split('-').slice(-1)[0];
  const groupAv = (d.group && d.group.length === 2) ? segAvatarsHtml(d.group, color) : '';
  el.innerHTML =
    `<div class="da-trio">${groupAv}${segAvatarsHtml(d.animals, color)}</div>` +
    `<div class="da-body"><div class="da-name">${names}</div><div class="da-tail">${tail} ›</div></div>`;
  el.style.display = '';
}

// The Device sub-view's Forget action (was the ⋮ "Forget this device").
export function forgetCurrentDevice(){
  // No window.confirm() guard (see forgetRecentDevice): it is suppressed in
  // standalone/PWA contexts and silently no-oped the action. Forget proceeds
  // unconditionally; the toast at the end is the feedback.
  // V1: clear local state only. The server-side pings stay until a DELETE
  // endpoint lands; meanwhile the user can't see them without the token,
  // which they've just discarded.
  const forgottenToken = MOCK.device?.token;
  closeDrawer();
  clearStoredToken();
  if(forgottenToken){
    forgetFromHistory(forgottenToken);
    clearKeypair(forgottenToken);
    clearActiveSession(forgottenToken);
  }
  if(state.recording) stopRecording();
  stopCohortLive();
  MOCK.device = null;
  state.activeSession = null;
  state.cohort.clear();
  MOCK.pings = [];
  state.slots = [null, null, null];
  state.group = [null, null];
  state.targetOverride = null;
  state.viewMode = 'sessions';
  renderSlots();
  show('create');
  showToast('device forgotten');
}

// Re-render whichever auth-bearing sub-view is open (used by the auth/claim
// callbacks). No-op when the drawer is closed or on a non-auth view.
export function refreshAuthUi(){
  if(!drawerEl.classList.contains('is-open')) return;
  if(drawerView === 'account') renderAccountPanel();
  else if(drawerView === 'device') renderDevicePanel();
}
